import { useState } from 'react';
import { ShieldAlert, ShieldCheck } from 'lucide-react';
import type { KillSwitchInfo } from '../types';
import { activateKillSwitch, deactivateKillSwitch } from '../api';

interface KillSwitchProps {
  info: KillSwitchInfo;
  onUpdate: () => void;
}

export default function KillSwitch({ info, onUpdate }: KillSwitchProps) {
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleActivate = async () => {
    setLoading(true);
    try {
      await activateKillSwitch('dashboard manual activation');
      onUpdate();
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  const handleDeactivate = async () => {
    setLoading(true);
    try {
      await deactivateKillSwitch();
      onUpdate();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-trader-card border border-trader-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-trader-text flex items-center gap-2">
          {info.active ? (
            <ShieldAlert size={18} className="text-trader-loss" />
          ) : (
            <ShieldCheck size={18} className="text-trader-profit" />
          )}
          Kill Switch
        </h3>
        <span
          className={`px-2 py-0.5 rounded text-xs font-bold ${
            info.active
              ? 'bg-trader-loss/20 text-trader-loss kill-switch-active'
              : 'bg-trader-profit/20 text-trader-profit'
          }`}
        >
          {info.active ? 'ACTIVE' : 'INACTIVE'}
        </span>
      </div>

      {info.active && (
        <div className="text-xs text-trader-muted mb-3 space-y-1">
          <p>Reason: {info.reason || 'unknown'}</p>
          <p>Since: {info.activated_at || 'unknown'}</p>
        </div>
      )}

      {showConfirm ? (
        <div className="bg-trader-loss/10 border border-trader-loss/30 rounded p-3 space-y-2">
          <p className="text-xs text-trader-loss">Activate kill switch? This will halt ALL trading.</p>
          <div className="flex gap-2">
            <button
              onClick={handleActivate}
              disabled={loading}
              className="px-3 py-1 text-xs rounded bg-trader-loss text-black font-bold hover:bg-trader-loss/80 disabled:opacity-50"
            >
              {loading ? 'Activating...' : 'CONFIRM'}
            </button>
            <button
              onClick={() => setShowConfirm(false)}
              className="px-3 py-1 text-xs rounded border border-trader-border text-trader-muted hover:text-trader-text"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          {info.active ? (
            <button
              onClick={handleDeactivate}
              disabled={loading}
              className="px-3 py-1 text-xs rounded border border-trader-profit text-trader-profit hover:bg-trader-profit hover:text-black disabled:opacity-50"
            >
              {loading ? 'Deactivating...' : 'Deactivate'}
            </button>
          ) : (
            <button
              onClick={() => setShowConfirm(true)}
              className="px-3 py-1 text-xs rounded border border-trader-loss text-trader-loss hover:bg-trader-loss hover:text-black"
            >
              Activate
            </button>
          )}
        </div>
      )}
    </div>
  );
}
